import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  Sparkles,
  Images,
  BookOpen,
  Coins,
  Rocket,
  Settings,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { isAuthed, clearAuth, getStoredUser } from "@/lib/auth";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/composer", label: "Composer", icon: Sparkles },
  { to: "/admin/library", label: "Library", icon: Images },
  { to: "/admin/skool-playbook", label: "Skool Playbook", icon: BookOpen },
  { to: "/admin/monetization", label: "Monetization", icon: Coins },
  { to: "/admin/launch-kit", label: "Launch Kit", icon: Rocket },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const user = getStoredUser();

  useEffect(() => {
    if (!isAuthed()) {
      const next = `${location.pathname}${location.search}` || "/admin";
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    setChecked(true);
  }, [navigate, location.pathname, location.search]);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  if (!checked) {
    return (
      <div
        data-testid="admin-loading"
        className="flex min-h-screen items-center justify-center bg-[#3D3A33] text-[#C9BEA8]"
      >
        Checking access…
      </div>
    );
  }

  const signOut = () => {
    clearAuth();
    navigate("/login", { replace: true });
  };

  const env = process.env.NODE_ENV === "production" ? "prod" : "dev";

  const SidebarInner = (
    <>
      <div className="px-6 pt-8 pb-6">
        <Link to="/admin" className="flex items-center gap-2.5" data-testid="admin-brand">
          <img
            src="/logo-transparent.png"
            alt="Coastal Roots Botanica"
            className="h-10 w-10"
            width={40}
            height={40}
          />
          <div className="leading-tight">
            <p className="font-serif text-lg tracking-wide text-[#F5EFE2]">Coastal Roots</p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">
              Command Center
            </p>
          </div>
        </Link>
      </div>

      <nav className="flex-1 space-y-1 px-4">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 rounded-lg py-2.5 pr-3 text-sm transition-colors",
                isActive ? "admin-nav-active" : "admin-nav-inactive",
              ].join(" ")
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[#5E594E] px-4 py-5">
        <div className="admin-user-tile mb-3 flex items-center gap-3 px-3 py-2.5">
          <img
            src="/logo-transparent.png"
            alt=""
            className="h-9 w-9 rounded-full border border-[#C9A961]/40"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-medium text-[#F5EFE2]">{user?.name || "Kenny"}</p>
            <p className="truncate text-[11px] text-[#9A8E78]">{user?.email || "kenny@coastalrootsbotanica.com"}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={signOut}
          data-testid="admin-sign-out"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-[#C9BEA8] transition-colors hover:bg-[#6E6656] hover:text-[#F5EFE2]"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#3D3A33] text-[#F5EFE2]" data-testid="admin-layout">
      {/* Top bar (mobile) */}
      <div className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-[#5E594E] bg-[#3D3A33] px-4 py-3 md:hidden">
        <Link to="/admin" className="flex items-center gap-2">
          <img src="/logo-transparent.png" alt="" className="h-8 w-8" />
          <span className="font-serif text-lg tracking-wide">Command Center</span>
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="rounded p-2 text-[#F5EFE2]"
          data-testid="admin-mobile-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div className="flex min-h-screen">
        {/* Sidebar (desktop) */}
        <aside
          data-testid="admin-sidebar"
          className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-[#5E594E] bg-[#5A5347] md:flex"
        >
          {SidebarInner}
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <aside
            data-testid="admin-sidebar-mobile"
            className="fixed inset-0 z-50 flex flex-col border-r border-[#5E594E] bg-[#5A5347] md:hidden"
          >
            <div className="flex items-center justify-between px-4 py-3">
              <span className="font-serif text-lg tracking-wide">Command Center</span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="rounded p-2 text-[#F5EFE2]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {SidebarInner}
          </aside>
        )}

        {/* Main */}
        <main className="min-w-0 flex-1">
          <header className="hidden items-center justify-between border-b border-[#5E594E] px-8 py-4 md:flex">
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-[0.22em] text-[#9A8E78]">
                {env === "prod" ? "Production" : "Preview · dev"}
              </span>
              <span className="rounded-full border border-[#C9A961]/50 bg-[#C9A961]/10 px-2 py-0.5 text-xs uppercase tracking-wider text-[#C9A961]">
                Owner
              </span>
            </div>
            <div className="text-sm text-[#C9BEA8]">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </div>
          </header>
          <div className="px-6 py-8 md:px-10 md:py-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
